import React, { useCallback, useState, useRef, useMemo } from 'react'

import { useFormikContext } from 'formik'

import { withStaticProperties, YStack, StackProps } from 'tamagui'

import { Button, ButtonProps } from '../Button/Button'

export const FormSubmitButton = withStaticProperties(
  ({
    children,
    alignItems = 'flex-end',
    secondary,
    buttonProps = {},
    ...props
  }: StackProps & { secondary?: boolean; buttonProps?: ButtonProps }) => {
    const formContextRef = useRef(useFormikContext())

    formContextRef.current = useFormikContext()

    const [forcedState, setForcedState] = useState<
      'notPressed' | 'pressed' | 'success' | 'error'
    >('notPressed')

    const onPress = useCallback(async () => {
      let submitError = false
      try {
        setForcedState('pressed')

        await formContextRef.current.submitForm()
      } catch {
        submitError = true
      } finally {
        if (
          submitError ||
          Object.keys(formContextRef.current.errors).length ||
          formContextRef.current.status
        ) {
          setForcedState('error')
        } else {
          setForcedState('success')
        }

        await new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve()
          }, 2000)
        })

        setForcedState('notPressed')
      }
    }, [setForcedState])

    // Wrap string children in Button.Text for proper styling
    const wrappedChildren = useMemo(() => {
      if (typeof children === 'string') {
        return <Button.Text>{children}</Button.Text>
      }
      return children
    }, [children])

    const isSecondary =
      secondary === undefined ? !formContextRef.current.isValid : secondary

    return (
      <YStack {...props} alignItems={alignItems}>
        <Button
          type='submit'
          height={38}
          {...buttonProps}
          onPress={onPress}
          secondary={isSecondary}
          forceState={
            formContextRef.current.isSubmitting ? 'pressed' : forcedState
          }
        >
          {wrappedChildren}
        </Button>
      </YStack>
    )
  },
  { Text: Button.Text }
)
